import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { motion } from "framer-motion";
export function MarkdownViewer({ url, className = "" }) {
    const [content, setContent] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    useEffect(() => {
        async function fetchMarkdown() {
            try {
                setLoading(true);
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`Failed to fetch document: ${response.statusText}`);
                }
                const text = await response.text();
                setContent(text);
            }
            catch (err) {
                console.error('Error fetching markdown:', err);
                setError(err.message || 'Failed to load document');
            }
            finally {
                setLoading(false);
            }
        }
        fetchMarkdown();
    }, [url]);
    if (loading) {
        return (_jsx("div", { className: `flex items-center justify-center py-12 ${className}`, children: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto mb-3" }), _jsx("p", { className: "text-sm text-gray-600 dark:text-gray-400", children: "Loading document..." })] }) }));
    }
    if (error) {
        return (_jsxs("div", { className: `py-12 text-center ${className}`, children: [_jsx("p", { className: "text-red-600 dark:text-red-400 mb-2", children: "Failed to load document" }), _jsx("p", { className: "text-sm text-gray-600 dark:text-gray-400", children: error })] }));
    }
    return (_jsxs(motion.div, { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.3 }, className: `markdown-document-viewer ${className}`, children: [_jsx("style", { children: `
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&family=Playfair+Display:wght@400;500;600;700;800&display=swap');

        .markdown-document-viewer {
          font-family: 'Montserrat', sans-serif;
          max-width: 900px;
          margin: 0 auto;
          padding: 3rem 2rem;
          background: white;
          color: #1a202c;
          line-height: 1.8;
          font-size: 16px;
          font-weight: 400;
        }

        .dark .markdown-document-viewer {
          background: #0d2139;
          color: #e2e8f0;
        }

        /* Headings with Playfair Display */
        .markdown-document-viewer h1 {
          font-family: 'Playfair Display', serif;
          font-size: 3rem;
          font-weight: 700;
          line-height: 1.2;
          margin-bottom: 1.5rem;
          margin-top: 3rem;
          color: #1a202c;
          letter-spacing: -0.02em;
          border-bottom: 3px solid #d97706;
          padding-bottom: 1rem;
        }

        .dark .markdown-document-viewer h1 {
          color: #f7fafc;
          border-bottom-color: #f59e0b;
        }

        .markdown-document-viewer h2 {
          font-family: 'Playfair Display', serif;
          font-size: 2.25rem;
          font-weight: 600;
          line-height: 1.3;
          margin-bottom: 1.25rem;
          margin-top: 2.5rem;
          color: #2d3748;
          letter-spacing: -0.01em;
          border-bottom: 2px solid #f59e0b;
          padding-bottom: 0.75rem;
        }

        .dark .markdown-document-viewer h2 {
          color: #edf2f7;
          border-bottom-color: #fbbf24;
        }

        .markdown-document-viewer h3 {
          font-family: 'Montserrat', sans-serif;
          font-size: 1.125rem;
          font-weight: 600;
          line-height: 1.6;
          margin-bottom: 0.75rem;
          margin-top: 1.5rem;
          color: #1a202c;
        }

        .dark .markdown-document-viewer h3 {
          color: #f7fafc;
        }

        .markdown-document-viewer h4 {
          font-family: 'Montserrat', sans-serif;
          font-size: 1.0rem;
          font-weight: 600;
          line-height: 1.6;
          margin-bottom: 0.5rem;
          margin-top: 1.25rem;
          color: #2d3748;
        }

        .dark .markdown-document-viewer h4 {
          color: #edf2f7;
        }

        .markdown-document-viewer h5 {
          font-family: 'Montserrat', sans-serif;
          font-size: 0.95rem;
          font-weight: 600;
          line-height: 1.6;
          margin-bottom: 0.5rem;
          margin-top: 1rem;
          color: #2d3748;
        }

        .dark .markdown-document-viewer h5 {
          color: #edf2f7;
        }

        .markdown-document-viewer h6 {
          font-family: 'Montserrat', sans-serif;
          font-size: 0.9rem;
          font-weight: 600;
          line-height: 1.6;
          margin-bottom: 0.5rem;
          margin-top: 0.875rem;
          color: #4a5568;
        }

        .dark .markdown-document-viewer h6 {
          color: #cbd5e0;
        }

        /* Paragraphs */
        .markdown-document-viewer p {
          margin-bottom: 1.5rem;
          text-align: justify;
          hyphens: auto;
        }

        /* Strong emphasis */
        .markdown-document-viewer strong {
          font-weight: 600;
          color: #1a202c;
        }

        .dark .markdown-document-viewer strong {
          color: #f7fafc;
        }

        /* Italic emphasis */
        .markdown-document-viewer em {
          font-style: italic;
          color: #2d3748;
        }

        .dark .markdown-document-viewer em {
          color: #edf2f7;
        }

        /* Lists */
        .markdown-document-viewer ul,
        .markdown-document-viewer ol {
          margin-bottom: 1.5rem;
          padding-left: 2rem;
        }

        .markdown-document-viewer li {
          margin-bottom: 0.75rem;
          line-height: 1.8;
        }

        .markdown-document-viewer ul > li {
          list-style-type: disc;
        }

        .markdown-document-viewer ol > li {
          list-style-type: decimal;
        }

        .markdown-document-viewer ul ul > li {
          list-style-type: circle;
        }

        .markdown-document-viewer ol ol > li {
          list-style-type: lower-alpha;
        }

        /* Blockquotes - Legal citations */
        .markdown-document-viewer blockquote {
          margin: 2rem 0;
          padding: 1.5rem 2rem;
          border-left: 4px solid #d97706;
          background: #fef3c7;
          font-style: italic;
          color: #78350f;
        }

        .dark .markdown-document-viewer blockquote {
          background: #1e3a5f;
          border-left-color: #f59e0b;
          color: #fef3c7;
        }

        /* Code blocks - For legal references */
        .markdown-document-viewer code {
          font-family: 'Monaco', 'Courier New', monospace;
          background: #f7fafc;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
          font-size: 0.9em;
          color: #c53030;
        }

        .dark .markdown-document-viewer code {
          background: #1a2f4a;
          color: #fc8181;
        }

        .markdown-document-viewer pre {
          background: #f7fafc;
          padding: 1.5rem;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin-bottom: 1.5rem;
          border: 1px solid #e2e8f0;
        }

        .dark .markdown-document-viewer pre {
          background: #1a2f4a;
          border-color: #2d4a6f;
        }

        .markdown-document-viewer pre code {
          background: transparent;
          padding: 0;
          color: #2d3748;
        }

        .dark .markdown-document-viewer pre code {
          color: #e2e8f0;
        }

        /* Horizontal rules - Section dividers */
        .markdown-document-viewer hr {
          margin: 3rem 0;
          border: none;
          border-top: 2px solid #e2e8f0;
        }

        .dark .markdown-document-viewer hr {
          border-top-color: #2d4a6f;
        }

        /* Tables - For data presentation */
        .markdown-document-viewer table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 2rem;
          font-size: 0.95rem;
          border: 1px solid #e2e8f0;
        }

        .dark .markdown-document-viewer table {
          border-color: #2d4a6f;
        }

        .markdown-document-viewer th {
          background: #f7fafc;
          font-family: 'Montserrat', sans-serif;
          font-weight: 600;
          text-align: left;
          padding: 1rem;
          border: 1px solid #d97706;
          border-bottom: 2px solid #d97706;
          color: #1a202c;
        }

        .dark .markdown-document-viewer th {
          background: #1a2f4a;
          border-color: #f59e0b;
          border-bottom-color: #f59e0b;
          color: #f7fafc;
        }

        .markdown-document-viewer td {
          padding: 0.875rem 1rem;
          border: 1px solid #e2e8f0;
        }

        .dark .markdown-document-viewer td {
          border-color: #2d4a6f;
        }

        .markdown-document-viewer tbody tr:hover {
          background: #fffbeb;
        }

        .dark .markdown-document-viewer tbody tr:hover {
          background: #1e3a5f;
        }

        /* Links */
        .markdown-document-viewer a {
          color: #d97706;
          text-decoration: underline;
          transition: color 0.2s;
        }

        .markdown-document-viewer a:hover {
          color: #b45309;
        }

        .dark .markdown-document-viewer a {
          color: #fbbf24;
        }

        .dark .markdown-document-viewer a:hover {
          color: #f59e0b;
        }

        /* Images */
        .markdown-document-viewer img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          margin: 2rem 0;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        /* First paragraph special styling (like legal docs) */
        .markdown-document-viewer > p:first-of-type {
          font-size: 1.125rem;
          font-weight: 400;
          color: #2d3748;
          margin-bottom: 2rem;
        }

        .dark .markdown-document-viewer > p:first-of-type {
          color: #cbd5e0;
        }

        /* Nested lists indentation */
        .markdown-document-viewer ul ul,
        .markdown-document-viewer ol ol,
        .markdown-document-viewer ul ol,
        .markdown-document-viewer ol ul {
          margin-top: 0.5rem;
          margin-bottom: 0.5rem;
        }

        /* Section numbering for legal documents */
        .markdown-document-viewer h2::before {
          font-family: 'Montserrat', sans-serif;
          font-size: 0.875rem;
          font-weight: 500;
          color: #d97706;
          display: block;
          margin-bottom: 0.5rem;
          text-transform: uppercase;
          letter-spacing: 0.1em;
        }

        .dark .markdown-document-viewer h2::before {
          color: #fbbf24;
        }
      ` }), _jsx(ReactMarkdown, { remarkPlugins: [remarkGfm], components: {
                    h1: ({ node, ...props }) => _jsx("h1", { ...props }),
                    h2: ({ node, ...props }) => _jsx("h2", { ...props }),
                    h3: ({ node, ...props }) => _jsx("h3", { ...props }),
                    h4: ({ node, ...props }) => _jsx("h4", { ...props }),
                    h5: ({ node, ...props }) => _jsx("h5", { ...props }),
                    h6: ({ node, ...props }) => _jsx("h6", { ...props }),
                    p: ({ node, ...props }) => _jsx("p", { ...props }),
                    a: ({ node, ...props }) => _jsx("a", { ...props, target: "_blank", rel: "noopener noreferrer" }),
                    ul: ({ node, ...props }) => _jsx("ul", { ...props }),
                    ol: ({ node, ...props }) => _jsx("ol", { ...props }),
                    li: ({ node, ...props }) => _jsx("li", { ...props }),
                    blockquote: ({ node, ...props }) => _jsx("blockquote", { ...props }),
                    code: ({ node, inline, ...props }) => inline ? _jsx("code", { ...props }) : _jsx("pre", { children: _jsx("code", { ...props }) }),
                    hr: ({ node, ...props }) => _jsx("hr", { ...props }),
                    table: ({ node, ...props }) => _jsx("table", { ...props }),
                    thead: ({ node, ...props }) => _jsx("thead", { ...props }),
                    tbody: ({ node, ...props }) => _jsx("tbody", { ...props }),
                    tr: ({ node, ...props }) => _jsx("tr", { ...props }),
                    th: ({ node, ...props }) => _jsx("th", { ...props }),
                    td: ({ node, ...props }) => _jsx("td", { ...props }),
                    img: ({ node, ...props }) => _jsx("img", { ...props, alt: props.alt || "Document image" }),
                    strong: ({ node, ...props }) => _jsx("strong", { ...props }),
                    em: ({ node, ...props }) => _jsx("em", { ...props }),
                }, children: content })] }));
}
